import React, { useRef, useCallback, useMemo } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { colors } from "../theme";
import { computeLeaseUpSchedule, LeaseUpRow } from "../utils/leaseUpCalc";

interface LeaseUpScheduleProps {
  constructionEndMonth: number;
  totalUnits: number;
  totalMonthsOfLeaseUp: number;
}

const COL_WIDTH = 80;
const SCROLL_COLS = 6;

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 140,
        p: 2,
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: 2,
        textAlign: "center",
      }}
    >
      <Typography variant="caption" sx={{ color: colors.grey[600], display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: colors.navy }}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function LeaseUpSchedule({
  constructionEndMonth,
  totalUnits,
  totalMonthsOfLeaseUp,
}: LeaseUpScheduleProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rows: LeaseUpRow[] = useMemo(
    () => computeLeaseUpSchedule(constructionEndMonth, totalUnits, totalMonthsOfLeaseUp),
    [constructionEndMonth, totalUnits, totalMonthsOfLeaseUp],
  );

  const scroll = useCallback((dir: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: dir * COL_WIDTH * SCROLL_COLS, behavior: "smooth" });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight") scroll(1);
      else if (e.key === "ArrowLeft") scroll(-1);
    },
    [scroll],
  );

  const avgPerMonth = useMemo(
    () => (totalMonthsOfLeaseUp > 0 ? Math.floor(totalUnits / totalMonthsOfLeaseUp) : 0),
    [totalUnits, totalMonthsOfLeaseUp],
  );

  return (
    <Box sx={{ mt: 2 }}>
      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <StatCard label="Construction Ends" value={`Mo. ${constructionEndMonth}`} />
        <StatCard label="Total Units to Lease" value={totalUnits} />
        <StatCard label="Avg Leases / Month" value={avgPerMonth} />
      </Box>

      {/* Scrollable table with chevron navigation */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          onClick={() => scroll(-1)}
          size="small"
          aria-label="Scroll schedule left"
          sx={{ flexShrink: 0 }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          ref={scrollRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          aria-label="Lease-up schedule table"
          role="region"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
            maxHeight: 320,
            borderRadius: 1,
            border: `1px solid ${colors.grey[300]}`,
            "&:focus-visible": { outline: `2px solid ${colors.blue}`, outlineOffset: 2 },
            scrollbarWidth: "thin",
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              minWidth: (rows.length + 1) * COL_WIDTH,
            }}
          >
            <thead>
              <tr>
                <th style={stickyCornerStyle}>&nbsp;</th>
                {rows.map((r) => (
                  <th key={r.month} style={headerCellStyle}>
                    Mo. {r.month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* New Leases */}
              <tr>
                <td style={rowLabelStyle}>New Leases</td>
                {rows.map((r, i) => (
                  <td key={r.month} style={cellStyle(i === 0)}>
                    {i === 0 ? "–" : r.newLeases}
                  </td>
                ))}
              </tr>
              {/* Total Leases */}
              <tr>
                <td style={rowLabelStyle}>Total Leases</td>
                {rows.map((r) => (
                  <td key={r.month} style={cellStyle(false)}>
                    {r.totalLeases}
                  </td>
                ))}
              </tr>
              {/* % Leased */}
              <tr>
                <td style={rowLabelStyle}>% Leased</td>
                {rows.map((r) => (
                  <td
                    key={r.month}
                    style={{
                      ...cellStyle(false),
                      fontWeight: 600,
                      color: r.pctLeased === 100 ? colors.success : colors.grey[900],
                    }}
                  >
                    {r.pctLeased}%
                  </td>
                ))}
              </tr>
              {/* Vacancy row */}
              <tr style={{ backgroundColor: colors.blueTint }}>
                <td style={{ ...rowLabelStyle, backgroundColor: colors.blueTint, fontWeight: 700 }}>
                  Vacancy
                </td>
                {rows.map((r, i) => (
                  <td
                    key={r.month}
                    style={{
                      ...cellStyle(false),
                      backgroundColor: colors.blueTint,
                      fontWeight: 600,
                      color: r.pctVacant === 0 ? colors.success : colors.grey[900],
                    }}
                  >
                    {i === 0 ? `${r.vacantUnits} units` : `${r.vacantUnits} (${r.pctVacant}%)`}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Box>

        <IconButton
          onClick={() => scroll(1)}
          size="small"
          aria-label="Scroll schedule right"
          sx={{ flexShrink: 0 }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

/* ---- style helpers ---- */

const stickyCornerStyle: React.CSSProperties = {
  position: "sticky",
  left: 0,
  top: 0,
  zIndex: 3,
  backgroundColor: colors.navy,
  color: colors.white,
  padding: "8px 12px",
  fontSize: "0.75rem",
  fontWeight: 600,
  textAlign: "left",
  minWidth: 120,
  borderRight: `1px solid ${colors.grey[300]}`,
};

const headerCellStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  backgroundColor: colors.navy,
  color: colors.white,
  padding: "8px 12px",
  fontSize: "0.75rem",
  fontWeight: 600,
  textAlign: "center",
  minWidth: COL_WIDTH,
  whiteSpace: "nowrap",
};

const rowLabelStyle: React.CSSProperties = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  backgroundColor: colors.white,
  padding: "8px 12px",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: colors.grey[700],
  textAlign: "left",
  minWidth: 120,
  borderRight: `1px solid ${colors.grey[300]}`,
  borderBottom: `1px solid ${colors.grey[300]}`,
};

const cellStyle = (dimmed: boolean): React.CSSProperties => ({
  padding: "8px 12px",
  fontSize: "0.875rem",
  textAlign: "center",
  borderBottom: `1px solid ${colors.grey[300]}`,
  color: dimmed ? colors.grey[400] : colors.grey[900],
  whiteSpace: "nowrap",
});
